import React, { useCallback, useEffect, useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableRow, IconButton, Tooltip } from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { fileService, LegacyFile } from '../../services/fileService';
import {
  SortableTableHeaderCell,
  FilesTableContainer,
  FilesTablePagination,
  FilesEmptyState,
} from './FilesTableShared';

export interface LegacyFilesPanelProps {
  /** Free-text filter from the shared search box. */
  search: string;
  /** Licensee filter from the shared LicenseeFilter; '' means all. */
  companyId: number | '';
  /** Reports the row count so the shared filter card can show "N files total". */
  onTotalChange: (total: number) => void;
  onError: (message: string) => void;
}

type SortColumn = 'fileName' | 'uploadedAt';

const formatSize = (bytes: number): string => {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
};

const SEARCH_DEBOUNCE_MS = 600;

const LegacyFilesPanel: React.FC<LegacyFilesPanelProps> = ({
  search,
  companyId,
  onTotalChange,
  onError,
}) => {
  const [files, setFiles] = useState<LegacyFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState<SortColumn>('uploadedAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Mirrors the `search` prop into local state on a 600ms debounce, matching
  // CalculatedFilesPage's own search debounce so the two views feel
  // identical. The parent forwards the raw shared search box value on every
  // keystroke; without this, `fetchFiles` (which depends on `search`) would
  // fire a COUNT(*) + ILIKE scan over the archive per keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  // Monotonically increasing id captured per fetch. No AbortController — the
  // guard below simply ignores the result of any request that is no longer
  // the latest one in flight, so a late-arriving response for a stale query
  // (or a stale page) can never clobber newer results or report a stale
  // count to the parent's "N files total".
  const requestIdRef = React.useRef(0);

  // Takes the page explicitly rather than closing over `page` state, so
  // callers (the settings-changed effect below, and the pagination handler)
  // can fetch the exact page they mean without waiting for a state update
  // to land first.
  const fetchFiles = useCallback(async (pageArg: number) => {
    const requestId = ++requestIdRef.current;
    try {
      setLoading(true);
      const result = await fileService.getLegacyFiles({
        page: pageArg + 1,
        limit: rowsPerPage,
        search: debouncedSearch || undefined,
        companyId: companyId === '' ? undefined : companyId,
        sortBy,
        sortDirection,
      });
      if (requestId !== requestIdRef.current) return;
      setFiles(result.data);
      setTotal(result.pagination.total);
      onTotalChange(result.pagination.total);
    } catch {
      if (requestId !== requestIdRef.current) return;
      onError('Failed to load legacy files');
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [rowsPerPage, debouncedSearch, companyId, sortBy, sortDirection, onTotalChange, onError]);

  // fetchFiles' identity changes exactly when a "setting" (debounced search,
  // licensee, page size, or sort) changes, or on mount. Any such change
  // invalidates the current page, so reset and refetch page 0 in one direct
  // call here — the single place that owns "settings changed" — instead of
  // racing a separate page-reset effect against a fetch effect (which could
  // fetch a stale page before the reset was applied). Explicit pagination
  // (handlePageChange) fetches directly too, so this effect never needs
  // `page` as a dependency.
  useEffect(() => {
    setPage(0);
    fetchFiles(0);
  }, [fetchFiles]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    fetchFiles(newPage);
  };

  const handleSort = (column: string) => {
    const col = column as SortColumn;
    if (sortBy === col) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDirection(col === 'fileName' ? 'asc' : 'desc');
    }
  };

  const handleDownload = async (file: LegacyFile) => {
    try {
      await fileService.downloadLegacyFile(file.id);
    } catch {
      onError('Failed to download legacy file');
    }
  };

  return (
    <>
      <FilesTableContainer loading={loading}>
        <Table>
          <TableHead>
            <TableRow>
              <SortableTableHeaderCell
                columnKey="fileName"
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={handleSort}
                defaultDirection="asc"
              >
                File Name
              </SortableTableHeaderCell>
              <TableCell>Licensee</TableCell>
              <SortableTableHeaderCell
                columnKey="uploadedAt"
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={handleSort}
                defaultDirection="desc"
              >
                Uploaded
              </SortableTableHeaderCell>
              <TableCell>Size</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {files.map((file) => (
              <TableRow key={file.id} hover>
                <TableCell sx={{ wordBreak: 'break-word' }}>{file.displayName}</TableCell>
                <TableCell>{file.companyName}</TableCell>
                <TableCell>
                  {file.uploadedAt ? file.uploadedAt.toLocaleDateString() : '—'}
                </TableCell>
                <TableCell>{formatSize(file.fileSize)}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Download">
                    <IconButton
                      size="small"
                      aria-label={`Download ${file.displayName}`}
                      onClick={() => handleDownload(file)}
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </FilesTableContainer>
      <FilesTablePagination
        total={total}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={setRowsPerPage}
      />
      <FilesEmptyState
        loading={loading}
        filesLength={files.length}
        primaryText="No legacy files found"
        secondaryText="Archived files from the previous CarbonCare system appear here."
      />
    </>
  );
};

export default LegacyFilesPanel;
